import React, {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {defaultErrorHandler} from '../../context/context';
import IllegalArgumentError from '../../domain/illegal-argument-error';
import customFormsV2Client from '../../infrastructure/acerill/custom-forms-v2-client';
import FormPanel from './form-panel';
import FormStyle from './form-style';
import FormItems from './form-items';
import FormValidation from './form-validation';
import FormSubmission from './submission/form-submission';
import FormPreview from './form-preview';
import SaveCancelBar from '../../libs/react-components-admin/buttons/save-cancel-bar';
import CenteredSpinner from '../../libs/react-components-admin/spinners/centered-spinner';
import Section from '../../libs/react-components-admin/section/section';
import FieldConditions from './field-condition';
import SubSection from '../../libs/react-components-admin/section/sub-section';
import {TextField} from '../../libs/react-components-admin/fields/text-like-field';
import PopupConfig from './popup-config';
import FormNoteForDevelopers from './note-for-developers';

const FormEdit = () => {

  const {formId} = useParams();

  const [model, setModel] = useState();

  const loadForm = () => {
    setModel(null);
    return customFormsV2Client
      .findFormById(formId)
      .then(form => {
        if (!form) {
          throw new IllegalArgumentError('No form found for ID ' + formId);
        }
        setModel(form);
      });
  };

  useEffect(() => { loadForm().catch(defaultErrorHandler); }, []);

  const sendTestEmail = emailAddress => customFormsV2Client.sendSubmissionNotificationTest({
    form: model,
    emails: [{address: emailAddress}],
  });

  const save = () => customFormsV2Client.updateForm(model);

  if (!model) {
    return <CenteredSpinner/>;
  }
  return <div className="row" style={{marginTop: '25px', marginBottom: '40px'}}>
    <div className="col-12 col-md-7">
      <Section title="Form settings" noMargin={true}>
        <TextField id="form-name"
                   label="Name"
                   placeholder="Enter a name"
                   helpBlock={<>Helps you identify this form (does not appear on your store page)</>}
                   value={model.name}
                   onChange={v => setModel({...model, name: v})}/>
        <FormItems conditions={model.fieldConditions}
                   model={model.fields}
                   onChange={v => setModel({...model, fields: v})}/>
        <FieldConditions className="my-5"
                         fields={model.fields}
                         model={model.fieldConditions}
                         onChange={v => setModel({...model, fieldConditions: v})}/>
        <FormPanel value={model.panel}
                   onChange={v => setModel(previousModel => {
                     const newModel = {...previousModel, panel: v};
                     if (v.enabled && (previousModel.popupConfig || {}).enabled) {
                       newModel.popupConfig = {...previousModel.popupConfig, enabled: false};
                     }
                     return newModel;
                   })}/>
        <PopupConfig value={model.popupConfig}
                     onChange={v => setModel(previousModel => {
                       const newModel = {...previousModel, popupConfig: v};
                       if (v.enabled && (previousModel.panel || {}).enabled) {
                         newModel.panel = {...previousModel.panel, enabled: false};
                       }
                       return newModel;
                     })}/>
        <SubSection title="Styles">
          <div className="mb-2 text-muted">
            <span className="material-icons align-bottom text-info">info</span> Leave blank to use your theme defaults
          </div>
          <FormStyle panelEnabled={(model.panel || {}).enabled}
                     value={model.style}
                     onChange={v => setModel({...model, style: v})}/>
        </SubSection>
        <FormValidation value={model.validation}
                        onChange={v => setModel({...model, validation: v})}/>
        <FormSubmission initialModel={model.submission}
                        onChange={v => setModel({...model, submission: v})}
                        onSendTestEmail={email => sendTestEmail(email)}/>
        <FormNoteForDevelopers/>
        <SaveCancelBar saveAction={() => save()}
                       cancelAction={() => loadForm()}
                       errorHandler={defaultErrorHandler}/>
      </Section>
    </div>
    <div className="hidden-xs hidden-sm col-md-5">
      <FormPreview form={model}/>
    </div>
  </div>;
};

export default FormEdit;
